/**
 * WorkforceOps — the live day-of board: walk-in queues, today's appointments,
 * today's events and who's on the floor, in one glance. Read-only aggregation
 * (actions live in Event Day / the queue tools); refreshes every 60s.
 * Data: getOpsBoard (declared op).
 */
import { useState, type ReactNode } from "react";
import { Icon, Pill, T, alpha } from "@v0y4ge/jrni-ui";
import { Screen, PageHeader } from "../../shell/chrome";
import { DataSourceStrip } from "../../shell/opSource";
import { StatTile, FilterBar, EmptyState } from "../../shell/composites";
import { useIsMobile } from "../../shell/useResponsive";
import { friendlyTime } from "../../lib/datetime";
import { useWfResource } from "./useWorkforce";
import { getOpsBoard, type OpsBoardSnapshot, type OpsAppointment, type OpsFloorEntry } from "./wf-api";
import { LocationFilter, WfPending, TinyProgress, isoToday, hmLabel, hmRangeLabel } from "./wf-shared";
import { useT } from "../../lib/i18n";

const APPT_PILL: Record<OpsAppointment["status"], { tone: "success" | "warn" | "danger" | "info" | "neutral"; label: string; labelKey: string }> = {
  "upcoming": { tone: "neutral", label: "Upcoming", labelKey: "common.status.upcoming" },
  "checked-in": { tone: "info", label: "Checked in", labelKey: "workforce.ops.checkedIn" },
  "in-progress": { tone: "success", label: "In progress", labelKey: "workforce.ops.inProgress" },
  "done": { tone: "neutral", label: "Done", labelKey: "common.actions.done" },
  "no-show": { tone: "danger", label: "No-show", labelKey: "workforce.ops.noShow" },
};
const FLOOR_META: Record<OpsFloorEntry["status"], { color: string; label: string; labelKey: string }> = {
  "on-shift": { color: T.green1, label: "On shift", labelKey: "workforce.ops.onShift" },
  "break": { color: T.orange1, label: "On break", labelKey: "workforce.ops.onBreak" },
  "off-soon": { color: T.tDarkBlue, label: "Off soon", labelKey: "workforce.ops.offSoon" },
  "late": { color: T.red1, label: "Late", labelKey: "workforce.ops.late" },
};

function Panel({ title, count, children }: { title: string; count?: string; children: ReactNode }) {
  return (
    <div style={{ background: T.white, border: `1px solid ${T.border}`, borderRadius: 8, boxShadow: T.shadowSm, display: "flex", flexDirection: "column", minHeight: 0 }}>
      <div style={{ padding: "12px 16px", borderBottom: `1px solid ${T.border}`, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <span style={{ fontSize: 13.5, fontWeight: 700, color: T.n1 }}>{title}</span>
        {count ? <span style={{ fontSize: 11.5, color: T.soft, fontFamily: T.fontMono }}>{count}</span> : null}
      </div>
      <div style={{ flex: 1, overflow: "auto", padding: 12, display: "flex", flexDirection: "column", gap: 10 }}>{children}</div>
    </div>
  );
}

export default function WorkforceOps() {
  const t = useT();
  const isMobile = useIsMobile();
  const [locationId, setLocationId] = useState<string | null>(null);
  const date = isoToday();
  const { data, loading, error, reload } = useWfResource<OpsBoardSnapshot>(
    `ops:${date}|${locationId ?? "all"}`, () => getOpsBoard(date, locationId ?? undefined), { pollMs: 60_000 });

  const waiting = (data?.queues ?? []).reduce((s, q) => s + q.waiting, 0);
  const apptsLeft = (data?.appointments ?? []).filter((a) => a.status === "upcoming" || a.status === "checked-in").length;
  const onFloor = (data?.floor ?? []).filter((f) => f.status !== "late").length;
  const eventsLive = (data?.events ?? []).length;

  const board = data && (
    <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "repeat(4, minmax(0, 1fr))", gap: 14, alignItems: "stretch" }}>
      <Panel title={t("workforce.ops.walkInQueues", "Walk-in queues")} count={`${waiting} waiting`}>
        {data.queues.length === 0 ? <span style={{ fontSize: 12.5, color: T.soft, padding: 8 }}>{t("workforce.ops.noOpenQueuesToday", "No open queues today.")}</span> : data.queues.map((q) => {
          const short = q.staffed < q.needed;
          return (
            <div key={q.id} data-testid="wfops-queue-card" data-id={q.id} style={{ border: `1px solid ${short ? alpha(T.red1, 35) : T.border}`, borderRadius: 8, padding: "10px 12px", display: "flex", flexDirection: "column", gap: 8 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <span style={{ fontSize: 13, fontWeight: 700, color: T.n1, flex: 1 }}>{q.name}</span>
                <span style={{ fontSize: 12, fontFamily: T.fontMono, color: short ? T.red1 : T.soft }}>{q.staffed}/{q.needed} {t("workforce.ops.staffed", "staffed")}</span>
              </div>
              <div style={{ display: "flex", gap: 14, fontSize: 11.5, color: T.soft }}>
                <span><b style={{ color: T.n1, fontFamily: T.fontMono }}>{q.waiting}</b> {t("workforce.ops.waiting", "waiting")}</span>
                <span>{t("workforce.ops.avg", "avg")}{" "}<b style={{ color: T.n1, fontFamily: T.fontMono }}>{q.avgWaitMins}{t("workforce.ops.m", "m")}</b></span>
                <span>{t("workforce.ops.longest", "longest")}{" "}<b style={{ color: q.longestWaitMins > 20 ? T.red1 : T.n1, fontFamily: T.fontMono }}>{q.longestWaitMins}{t("workforce.ops.m", "m")}</b></span>
              </div>
              {q.entries.slice(0, 4).map((e) => (
                <div key={e.id} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12 }}>
                  <span style={{ width: 6, height: 6, borderRadius: "50%", background: e.status === "serving" ? T.green1 : e.status === "called" ? T.orange1 : T.n4, flexShrink: 0 }} />
                  <span style={{ color: T.n1, fontWeight: 600, flex: 1, minWidth: 0, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{e.name}</span>
                  {e.service ? <span style={{ color: T.soft, whiteSpace: "nowrap" }}>{e.service}</span> : null}
                  <span style={{ color: T.soft, fontFamily: T.fontMono }}>{friendlyTime(e.joinedAt, data.tz)}</span>
                </div>
              ))}
              {q.entries.length > 4 ? <span style={{ fontSize: 11, color: T.soft }}>+{q.entries.length - 4} {t("workforce.ops.moreInLine", "more in line")}</span> : null}
            </div>
          );
        })}
      </Panel>

      <Panel title={t("workforce.ops.appointmentsToday", "Appointments today")} count={`${apptsLeft} to go`}>
        {data.appointments.length === 0 ? <span style={{ fontSize: 12.5, color: T.soft, padding: 8 }}>{t("workforce.ops.noAppointmentsBookedToday", "No appointments booked today.")}</span> : data.appointments.map((a) => (
          <div key={a.id} data-testid="wfops-appt-row" data-id={a.id} style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 10px", borderRadius: 8, border: `1px solid ${T.n6}` }}>
            <span style={{ fontFamily: T.fontMono, fontSize: 12, color: T.n1, width: 62, flexShrink: 0 }}>{hmLabel(a.start)}</span>
            <span style={{ flex: 1, minWidth: 0 }}>
              <span style={{ display: "block", fontSize: 12.5, fontWeight: 600, color: T.n1, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{a.customer}</span>
              <span style={{ display: "block", fontSize: 11.5, color: T.soft, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{a.service} · {a.staffName}</span>
            </span>
            <Pill tone={APPT_PILL[a.status].tone}>{t(APPT_PILL[a.status].labelKey, APPT_PILL[a.status].label)}</Pill>
          </div>
        ))}
      </Panel>

      <Panel title={t("workforce.ops.eventsToday", "Events today")} count={`${eventsLive}`}>
        {data.events.length === 0 ? <span style={{ fontSize: 12.5, color: T.soft, padding: 8 }}>{t("workforce.ops.noEventsRunningToday", "No events running today.")}</span> : data.events.map((e) => {
          const short = e.staffOnShift < e.staffNeeded;
          return (
            <div key={e.id} data-testid="wfops-event-card" data-id={e.id} style={{ border: `1px solid ${short ? alpha(T.red1, 35) : T.border}`, borderRadius: 8, padding: "10px 12px", display: "flex", flexDirection: "column", gap: 8 }}>
              <span style={{ fontSize: 13, fontWeight: 700, color: T.n1 }}>{e.title}</span>
              <span style={{ fontSize: 11.5, color: T.soft }}>{hmRangeLabel(e.start, e.end)}</span>
              <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 11.5, color: T.soft }}>
                <span style={{ width: 78 }}>{t("workforce.ops.checkIns", "Check-ins")}</span><TinyProgress num={e.checkedIn} den={Math.max(1, e.registered)} />
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 11.5, color: short ? T.red1 : T.soft }}>
                <span style={{ width: 78 }}>{t("workforce.ops.staff", "Staff")}</span><TinyProgress num={e.staffOnShift} den={Math.max(1, e.staffNeeded)} />
              </div>
            </div>
          );
        })}
      </Panel>

      <Panel title={t("workforce.ops.onTheFloor", "On the floor")} count={`${onFloor} on shift`}>
        {data.floor.length === 0 ? <span style={{ fontSize: 12.5, color: T.soft, padding: 8 }}>{t("workforce.ops.nobodyScheduledRightNow", "Nobody scheduled right now.")}</span> : data.floor.map((f) => {
          const meta = FLOOR_META[f.status];
          return (
            <div key={f.staffId} data-testid="wfops-floor-row" data-id={f.staffId} style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <span style={{ width: 8, height: 8, borderRadius: "50%", background: meta.color, flexShrink: 0 }} />
              <span style={{ flex: 1, minWidth: 0 }}>
                <span style={{ display: "block", fontSize: 12.5, fontWeight: 600, color: T.n1, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{f.name}</span>
                <span style={{ display: "block", fontSize: 11.5, color: T.soft }}>{f.role}</span>
              </span>
              <span style={{ fontSize: 11.5, color: T.soft, whiteSpace: "nowrap" }}>{t(meta.labelKey, meta.label)} {t("workforce.ops.until", "· until")}{" "}{hmLabel(f.until)}</span>
            </div>
          );
        })}
      </Panel>
    </div>
  );

  return (
    <Screen rail="wf-ops">
      <PageHeader title={t("workforce.ops.opsBoard", "Ops board")} subtitle={t("workforce.ops.liveViewOfTodayAcrossQueuesAppointmentsEventsAndTheFloorAuto", "Live view of today across queues, appointments, events and the floor. Auto-refreshes every minute.")} breadcrumb={null}
        actions={<>
          {data ? <span style={{ fontSize: 12, color: T.soft }}>{t("workforce.ops.asOf", "As of")}{" "}{friendlyTime(data.asOf, data.tz)}</span> : null}
          <button data-testid="wfops-refresh-action" onClick={reload} aria-label={t("common.actions.refresh", "Refresh")}
            style={{ width: 34, height: 34, borderRadius: 6, border: `1px solid ${T.border}`, background: T.white, color: T.n2, cursor: "pointer", display: "inline-flex", alignItems: "center", justifyContent: "center", boxShadow: T.shadowChrome }}><Icon.refresh size={15} /></button>
        </>} />
      <DataSourceStrip screen="wfops" ops={["getOpsBoard"]} />
      <div style={{ padding: "16px 24px 0", display: "grid", gridTemplateColumns: isMobile ? "repeat(2, 1fr)" : "repeat(4, 1fr)", gap: 12 }}>
        <StatTile label={t("workforce.ops.waitingInQueues", "Waiting in queues")} value={waiting} icon={<Icon.list size={14} />} accent={waiting > 0 ? T.orange1 : T.green1} />
        <StatTile label={t("workforce.ops.appointmentsLeft", "Appointments left")} value={apptsLeft} icon={<Icon.clock size={14} />} accent={T.tDarkBlue} />
        <StatTile label={t("workforce.ops.eventsToday", "Events today")} value={eventsLive} icon={<Icon.ticket size={14} />} accent={T.tPurple} />
        <StatTile label={t("workforce.ops.onTheFloor", "On the floor")} value={onFloor} icon={<Icon.users size={14} />} accent={T.p1} />
      </div>
      <FilterBar>
        <LocationFilter value={locationId} onChange={setLocationId} />
        <span style={{ marginLeft: "auto", fontSize: 12, color: T.soft }}>{t("workforce.ops.today", "Today")}</span>
      </FilterBar>
      <div style={{ flex: 1, overflow: "auto", padding: "0 24px 24px" }}>
        {error ? <WfPending screen="wfops" opName="getOpsBoard" error={error} onRetry={reload} />
          : loading ? <div style={{ padding: 40, textAlign: "center", color: T.soft, fontSize: 14 }}>{t("workforce.ops.loadingTheBoard", "Loading the board…")}</div>
          : (data && data.queues.length === 0 && data.appointments.length === 0 && data.events.length === 0 && data.floor.length === 0)
            ? <EmptyState icon={<Icon.clock size={20} />} title={t("workforce.ops.quietDay", "Quiet day")} body={t("workforce.ops.noQueuesAppointmentsEventsOrShiftsAtThisLocationToday", "No queues, appointments, events or shifts at this location today.")} />
            : board}
      </div>
    </Screen>
  );
}
